import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, forkJoin, map, of, switchMap, take, tap, timeout } from 'rxjs';
import { DateTime } from 'luxon';
import { UserService } from '@core/user/user.service';
import { AbsenceRequestsService } from 'app/modules/admin/administration/absence-requests/absence-requests.service';
import { WorkTimeService } from 'app/modules/admin/pages/work-time/work-time.service';
import { Notification } from './notifications.types';
@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private readonly users = inject(UserService);
    private readonly absences = inject(AbsenceRequestsService);
    private readonly work = inject(WorkTimeService);
    private readonly state = new BehaviorSubject<Notification[]>([]);
    private key = '';
    private read = new Set<string>();
    private hidden = new Set<string>();
    private generation = 0;
    loadFailed = false;
    get notifications$(): Observable<Notification[]> { return this.state.asObservable(); }
    getAll(): Observable<Notification[]> {
        const generation = ++this.generation;
        return this.users.user$.pipe(take(1), switchMap(user => {
            const key = 'mzeit-notifications:' + user.tenantId + ':' + (user.userId || user.employeeId);
            if (this.key !== key) { this.key = key; this.state.next([]); this.read.clear(); this.hidden.clear();
                try { const saved = JSON.parse(localStorage.getItem(key) || '{}'); this.read = new Set(saved.read || []); this.hidden = new Set(saved.hidden || []); } catch { /* Storage is optional. */ }
            }
            this.loadFailed = false;
            const today = DateTime.local().startOf('day');
            const years = [...new Set([today.year, today.plus({ days: 30 }).year])];
            const fail = () => { if (generation === this.generation) this.loadFailed = true; return of([]); };
            const absences = forkJoin(years.map(year => this.absences.getRequests({ year }).pipe(timeout(10000))))
                .pipe(map(rows => rows.flat()), catchError(fail));
            const entries = this.work.getWorkTimeSessions({ from: today.minus({ days: 14 }).toISODate(), to: today.toISODate(), size: 20, sort: 'date', order: 'desc' }, false)
                .pipe(timeout(10000), map(result => result.data), catchError(fail));
            return forkJoin({ absences, entries }).pipe(map(result => {
                const notifications: Notification[] = [];
                const seen = new Set<string>();
                for (const row of result.absences) {
                    if (seen.has(row.id)) continue;
                    seen.add(row.id);
                    const pending = row.status === 'Requested';
                    const upcoming = row.status === 'Approved' && row.to.slice(0,10) >= today.toISODate() && row.from.slice(0,10) <= today.plus({days:30}).toISODate();
                    if (!pending && !upcoming) continue;
                    notifications.push({ id: 'absence:' + row.id + ':' + row.status + ':' + row.from + ':' + row.to,
                        title: pending ? 'Offener Abwesenheitsantrag' : 'Anstehende Abwesenheit',
                        description: row.employeeDisplayName + ' · ' + row.absenceTypeName + ' · ' + DateTime.fromISO(row.from).toFormat('dd.MM.yyyy') + ' – ' + DateTime.fromISO(row.to).toFormat('dd.MM.yyyy'),
                        time: row.from, icon: 'heroicons_outline:calendar-days', read: false, useRouter: true,
                        link: pending ? '/administration/absence-requests' : '/administration/absence-list' });
                }
                for (const row of result.entries) notifications.push({ id: 'work:' + row.id + ':' + row.status + ':' + row.totalMinutes + ':' + row.totalPauseMinutes,
                    title: row.isAutomaticallyStopped ? 'Automatisch gestoppter Zeiteintrag' : 'Aktueller Arbeitszeiteintrag',
                    description: row.employeeLastName + ', ' + row.employeeFirstName + ' · ' + DateTime.fromISO(row.date).toFormat('dd.MM.yyyy'),
                    time: row.date, icon: 'heroicons_outline:clock', read: false, useRouter: true,
                    link: '/work-time/sessions' });
                return notifications.filter(n => !this.hidden.has(n.id)).slice(0,60).map(n => ({ ...n, read: this.read.has(n.id) }));
            }));
        }), tap(rows => { if (generation === this.generation) this.state.next(rows); }));
    }
    update(id: string, notification: Notification): Observable<Notification> {
        notification.read ? this.read.add(id) : this.read.delete(id);
        this.state.next(this.state.value.map(row => row.id === id ? { ...row, read: notification.read } : row));
        this.save(); return of(notification);
    }
    delete(id: string): Observable<boolean> {
        this.hidden.add(id); this.state.next(this.state.value.filter(row => row.id !== id)); this.save(); return of(true);
    }
    markAllAsRead(): Observable<boolean> {
        this.state.value.forEach(row => this.read.add(row.id));
        this.state.next(this.state.value.map(row => ({ ...row, read: true }))); this.save(); return of(true);
    }
    private save(): void {
        try { localStorage.setItem(this.key, JSON.stringify({ read: [...this.read].slice(-500), hidden: [...this.hidden].slice(-500) })); } catch { /* Keep session state if storage is unavailable. */ }
    }
}
